// 裝置資訊 / 維運（cluster 0x8000, spec 21 §5）+ 韌體更新。
// - 資訊：op:get STAT rows（OnlineStatus / FirmwareVersion / Wifi / IP / Temp）
// - Reboot：單次確認；FactoryReset：雙重確認 + 「改用解除綁定」分流文案（§5.3）
// - OTA：REST check-upgrade → 確認 dialog → Signal OtaStart CMD(strVal=version)
//   → 進度經 Signaling room 事件 best-effort 顯示（APP 00 §7.4）。

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ApiError, apiFetch } from '../../../lib/api';
import { formatTimestampUs } from '../../../lib/format';
import { signaling } from '../../../lib/ws';
import type { SignalFrame } from '../../../lib/ws';
import { HEALTH_FACTORY_RESET, HEALTH_FW_VERSION, HEALTH_IP, HEALTH_ONLINE, HEALTH_OTA_START, HEALTH_REBOOT, HEALTH_RESET_KEEP_WIFI, HEALTH_TEMP, HEALTH_TIMEZONE, HEALTH_WIFI, REQUEST_REPORT } from './ids';
import { isPhoneCam } from '../../../lib/phoneCam';
import { PhoneCamRows } from './PhoneCamRows';
import { opCmd, useAttr } from './ops';
import { ONLINE_EVENT_ID } from '../deviceModel';
import { browserPosixDefault, labelForPosix, TIMEZONES } from './timezones';
import { ConfirmDialog, ReadonlyRow, RowShell, SectionCard } from './widgets';

interface CheckUpgradeData {
  needs_upgrade?: boolean;
  latest_version?: string;
  is_critical?: boolean;
  release_notes_url?: string;
}

// OTA 歷史（spec 21 §8）：GET /v3/devices/{id}/ota → domain.OTAJob[]（owner-gated）。
// As-built 回應形狀 = restful domain.OTAJob：job_ts / target_version / status /
// presigned_url_expire_at（後端尚未投影 §8 工作假設的 error / triggered_by → GAP-DS-4）。
interface OtaJob {
  job_ts: number;
  target_version: string;
  status: string;
  presigned_url_expire_at?: number;
}

// ⚠️ i18n: OTA 狀態顯示名須在使用點以 t 求值(語言切換要即時),不可凍結成模組頂層 const。
// key = 後端 status enum(別動),value = 顯示名。
function otaStatusLabel(t: TFunction, status: string): string | undefined {
  const map: Record<string, string> = {
    pending: t('deviceSettings.otaStatusPending'),
    downloading: t('deviceSettings.otaStatusDownloading'),
    installing: t('deviceSettings.otaStatusInstalling'),
    success: t('deviceSettings.otaStatusSuccess'),
    failed: t('deviceSettings.otaStatusFailed'),
    rolled_back: t('deviceSettings.otaStatusRolledBack'),
  };
  return map[status];
}

// OtaProgress 終態判定（對齊 iOS OtaProgressUpdate.isSuccess/isFailure）：升級中的進度事件帶到
// terminal token 時,狀態機須收斂,否則畫面永卡「更新進行中」。比對前一律小寫。
const OTA_SUCCESS_TOKENS = new Set(['success', 'succeeded', 'done', 'completed', 'complete', 'ota_success']);
const OTA_FAILURE_TOKENS = new Set(['failed', 'failure', 'error', 'rolled_back', 'rollback', 'ota_failed']);

// OtaSuccess EVENT id (0x11 event cluster). ⚠️ ≠ HEALTH_OTA_START CMD (0x31…0200)
// and ≠ HEALTH_RESET_KEEP_WIFI CMD (0x31…0300): same 0300 suffix, different 0x11/0x31
// prefix. Signaling broadcasts this when the device reports OtaSuccess *before*
// reboot; the server holds the job as "rebooting" (correlateOTAEvent) and only marks
// success after the post-reboot sign-in. So this event = "installing done, rebooting",
// NOT final success — the webapp mirrors that and confirms the outcome via ota_jobs.
const OTA_SUCCESS_EVENT_ID = '0x1100008000000300';

// 從 OtaProgress 事件的 status/phase/event_type 判定終態。成功優先於失敗（對齊 iOS
// handleOtaProgress:`if isSuccess … else error`）。非終態回 null（升級進行中）。
export function otaTerminalStatus(p: Record<string, unknown>): 'success' | 'failure' | null {
  const tokens: string[] = [];
  for (const k of ['status', 'phase', 'event_type']) {
    const v = p[k];
    if (typeof v === 'string' && v) tokens.push(v.toLowerCase());
  }
  if (tokens.some((t) => OTA_SUCCESS_TOKENS.has(t))) return 'success';
  if (tokens.some((t) => OTA_FAILURE_TOKENS.has(t))) return 'failure';
  return null;
}

function otaStatusColor(status: string): string {
  switch (status) {
    case 'success':
      return 'var(--color-semantic-success)';
    case 'failed':
      return 'var(--color-semantic-danger)';
    case 'rolled_back':
      return 'var(--color-semantic-warning)';
    default:
      return 'var(--color-text-secondary)';
  }
}

export function HealthSection({
  deviceId,
  isOwner,
  wsOpen,
  model,
  deviceTz,
  currentVersion,
  deviceName,
  onFirmwareUpdated,
}: {
  /** §5.2 維運、OTA、§5.1a 時區皆 owner 閘（時區:signaling tzsettings 驗 role=="owner"）。 */
  isOwner: boolean;
  deviceId: string;
  wsOpen: boolean;
  model: string;
  /** 裝置時區 POSIX（devices.tz）；手機攝影機讀不到時區屬性，用這個顯示（ls-loop MW）。 */
  deviceTz?: string;
  currentVersion: string;
  deviceName: string;
  /** OTA 確認成功後呼叫（此時後端 devices.firmware_version 已更新）→ 讓父頁 refetch，
   *  使裝置資訊總覽的「韌體版本 / 可更新至」跟著翻新（DeviceDetailPage load）。 */
  onFirmwareUpdated?: () => void;
}) {
  const { t } = useTranslation();
  const online = useAttr(deviceId, HEALTH_ONLINE, wsOpen);
  const fw = useAttr(deviceId, HEALTH_FW_VERSION, wsOpen);
  const wifi = useAttr(deviceId, HEALTH_WIFI, wsOpen);
  const ip = useAttr(deviceId, HEALTH_IP, wsOpen);
  const temp = useAttr(deviceId, HEALTH_TEMP, wsOpen);
  // Device timezone (common/22): SET str POSIX; op:set via useAttr.set (strVal).
  // NOT the IANA WeeklySchedule timezone (ADR-054). Picker offers curated POSIX
  // options; the browser/OS zone pre-selects when the device has no value yet.
  const tz = useAttr(deviceId, HEALTH_TIMEZONE, wsOpen);
  const tzValue = (typeof tz.value === 'string' && tz.value) ? tz.value : browserPosixDefault();
  // MyCam 手機攝影機（ls-loop MW）：沒有 Wi-Fi 訊號 / 溫度 / 重開機等；時區跟著那支手機（唯讀）
  const phone = isPhoneCam(model);

  const [dialog, setDialog] = useState<'reboot' | 'factory-reset' | 'reset-keep-wifi' | 'ota' | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // OTA state machine: idle → checking → (up-to-date | available) → upgrading →
  // rebooting → (up-to-date | error). "rebooting" is entered on the device's
  // pre-reboot OtaSuccess event; the true outcome is reconciled from ota_jobs.
  const [otaState, setOtaState] = useState<'idle' | 'checking' | 'up-to-date' | 'available' | 'upgrading' | 'rebooting' | 'error'>('idle');
  const [upgrade, setUpgrade] = useState<CheckUpgradeData | null>(null);
  const [otaError, setOtaError] = useState<string | null>(null);
  const [otaProgress, setOtaProgress] = useState<string[]>([]);

  // OTA 歷史（§8）— owner-only（端點 RequireOwner）；非 owner 不請求、任何錯誤即優雅隱藏。
  const [otaJobs, setOtaJobs] = useState<OtaJob[] | null>(null);
  // Target version of the in-flight upgrade (captured at trigger) so the rebooting
  // reconcile matches the exact ota_jobs row when several versions coexist.
  const otaTargetRef = useRef<string | null>(null);
  // Version the device is on after a successful OTA *this session*. currentVersion is
  // the device.firmware_version prop loaded once by the parent; it stays stale until a
  // reload, so once we confirm success we show the upgraded target locally. Reload
  // refreshes the prop (backend firmware_version is updated at post-reboot sign-in).
  const [installedVersion, setInstalledVersion] = useState<string | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const loadOtaJobs = useCallback(async () => {
    if (!isOwner) return; // 端點 RequireOwner（§8）；非 owner → 403，不請求
    try {
      const res = await apiFetch<OtaJob[]>(`/v3/devices/${deviceId}/ota`);
      if (!alive.current) return;
      const jobs = Array.isArray(res.data) ? res.data : [];
      // 後端依 job_ts 寫入；保險起見 client 再以時間倒序（newest first）。
      jobs.sort((a, b) => (b.job_ts ?? 0) - (a.job_ts ?? 0));
      setOtaJobs(jobs);
    } catch {
      // 端點不可用 / 403 / 網路 → 隱藏整段歷史（唯讀增益，不阻斷頁面）。
      if (alive.current) setOtaJobs(null);
    }
  }, [isOwner, deviceId]);

  useEffect(() => {
    void loadOtaJobs();
  }, [loadOtaJobs]);

  // OTA 進度：room 廣播事件 best-effort 顯示（download 即時送 client，00 §10）
  useEffect(() => {
    if (otaState !== 'upgrading') return;
    const off = signaling.on('event', (frame: SignalFrame) => {
      if (frame.device_id !== deviceId) return;
      const p = (frame.payload ?? frame) as Record<string, unknown>;
      const bits: string[] = [];
      for (const k of ['status', 'phase', 'progress', 'percent', 'message', 'event_type']) {
        const v = p[k];
        if (typeof v === 'string' || typeof v === 'number') bits.push(`${k}=${v}`);
      }
      if (bits.length > 0) {
        setOtaProgress((prev) => [...prev.slice(-9), `${new Date().toLocaleTimeString()} ${bits.join(' ')}`]);
      }
      // 裝置的 OtaSuccess（0x11…0300）在 reboot 之前送出；signaling 把它壓成
      // 「rebooting」而非最終成功（correlateOTAEvent），真正成功要等 reboot 後 sign-in。
      // 這裡鏡像該語意：進 rebooting，最終 success/failure 由下方 ota_jobs 對帳收斂
      // （reboot 後的權威成功事件不會廣播給 client，故不能只靠 WS）。
      const eventId = typeof frame.event_id === 'string' ? frame.event_id : '';
      if (eventId.toLowerCase() === OTA_SUCCESS_EVENT_ID) {
        setOtaState('rebooting');
        void loadOtaJobs();
        return;
      }
      // terminal 收尾（對齊 iOS handleOtaProgress）：升級到終態即翻狀態機 + 刷新歷史,否則畫面永卡
      // 「更新進行中」直到手動重整。成功 → up-to-date（裝置隨後重啟）;失敗 → error。
      const terminal = otaTerminalStatus(p);
      if (terminal === 'success') {
        if (otaTargetRef.current) setInstalledVersion(otaTargetRef.current);
        fw.refresh(); // 「韌體版本」STAT row 重新 op:get(裝置已在新版)
        onFirmwareUpdated?.(); // 父頁 refetch → 裝置資訊「韌體版本/可更新至」翻新
        setOtaState('up-to-date');
        void loadOtaJobs();
      } else if (terminal === 'failure') {
        setOtaError(t('deviceSettings.otaFailed'));
        setOtaState('error');
        void loadOtaJobs();
      }
    });
    return off;
  }, [otaState, deviceId, loadOtaJobs, t]);

  // 從 ota_jobs（server 權威）判定這次升級的終態。清單為 newest-first；優先對到我們
  // 觸發的 target_version（退而取最新一筆）。依 restful 的 success / failed / rolled_back，
  // 而非 reboot 前的 device OtaSuccess（那顆 server 壓成 rebooting）。
  const reconcileOtaOutcome = useCallback((jobs: OtaJob[]): 'success' | 'failure' | null => {
    const target = otaTargetRef.current;
    const job = (target ? jobs.find((j) => j.target_version === target) : undefined) ?? jobs[0];
    if (!job) return null;
    if (job.status === 'success') return 'success';
    if (job.status === 'failed' || job.status === 'rolled_back') return 'failure';
    return null;
  }, []);

  // rebooting：event-driven，不輪詢。裝置 reboot 後「重新上線」(presence ONLINE 事件) 才重抓一次
  // ota_jobs 收斂——因為 reboot 後裝置 sign-in 已把 firmware_version + ota_jobs 更新好(權威成功不
  // broadcast 給 client,故用既有 online 事件當觸發、而非計時輪詢)。online 廣播與後端寫入並行、
  // 可能上線略早,故上線後補少量短重試(非長輪詢)。online 若漏收,下次進頁 mount fetch 仍反映結果。
  useEffect(() => {
    if (otaState !== 'rebooting' || !isOwner) return;
    let cancelled = false;
    let retries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const MAX_RETRIES = 4; // 上線後防 online→寫入 race 的短重試(~10s),非長時間輪詢
    const RETRY_MS = 2500;
    const reconcile = async () => {
      if (cancelled) return;
      try {
        const res = await apiFetch<OtaJob[]>(`/v3/devices/${deviceId}/ota`);
        if (cancelled || !alive.current) return;
        const jobs = Array.isArray(res.data) ? res.data : [];
        jobs.sort((a, b) => (b.job_ts ?? 0) - (a.job_ts ?? 0));
        setOtaJobs(jobs);
        const outcome = reconcileOtaOutcome(jobs);
        if (outcome === 'success') {
          if (otaTargetRef.current) setInstalledVersion(otaTargetRef.current);
          fw.refresh(); // 「韌體版本」STAT row 重新 op:get(裝置已在新版)
          onFirmwareUpdated?.(); // 父頁 refetch → 裝置資訊「韌體版本/可更新至」翻新
          setOtaState('up-to-date');
          return;
        }
        if (outcome === 'failure') {
          setOtaError(t('deviceSettings.otaFailed'));
          setOtaState('error');
          return;
        }
      } catch {
        // 暫時性錯誤 → 視為未落庫,走下方短重試
      }
      if (!cancelled && retries++ < MAX_RETRIES) timer = setTimeout(() => void reconcile(), RETRY_MS);
    };
    // 觸發:裝置 reboot 後重新上線(presence ONLINE 事件,app 本來就收得到)。不計時輪詢。
    const off = signaling.on('event', (frame: SignalFrame) => {
      if (frame.device_id !== deviceId) return;
      const eid = typeof frame.event_id === 'string' ? frame.event_id.toLowerCase() : '';
      if (eid === ONLINE_EVENT_ID.toLowerCase()) void reconcile();
    });
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      off();
    };
  }, [otaState, isOwner, deviceId, reconcileOtaOutcome, t]);

  // Displayed / compared "current version": prefer the version confirmed installed this
  // session (currentVersion prop is loaded once and stays stale until a reload).
  const shownVersion = installedVersion ?? currentVersion;

  async function checkUpgrade() {
    setOtaState('checking');
    setOtaError(null);
    try {
      const res = await apiFetch<CheckUpgradeData>('/v3/firmware/check-upgrade', {
        method: 'POST',
        body: { device_id: deviceId, model, current_version: shownVersion },
      });
      if (!alive.current) return;
      if (res.data?.needs_upgrade) {
        setUpgrade(res.data);
        setOtaState('available');
      } else {
        setOtaState('up-to-date');
      }
    } catch (err) {
      if (!alive.current) return;
      setOtaError(err instanceof ApiError ? err.message : t('deviceSettings.otaCheckFailed'));
      setOtaState('error');
    }
  }

  async function triggerOta() {
    if (!upgrade?.latest_version) return;
    setBusy(true);
    try {
      await opCmd(deviceId, HEALTH_OTA_START, upgrade.latest_version);
      if (!alive.current) return;
      otaTargetRef.current = upgrade.latest_version; // reconcile 對筆用
      setDialog(null);
      setOtaProgress([]);
      setOtaState('upgrading');
      void loadOtaJobs(); // 新 job 已寫入 → 刷新歷史
    } catch (err) {
      if (!alive.current) return;
      setDialog(null);
      setOtaError(err instanceof ApiError ? t('deviceSettings.otaTriggerFailedReason', { msg: err.message }) : t('deviceSettings.otaTriggerFailed'));
      setOtaState('error');
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  async function requestReport() {
    setBusy(true);
    setMessage(null);
    try {
      await opCmd(deviceId, REQUEST_REPORT);
      setMessage(t('phonecam.requestReportSent'));
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  async function runCmd(kind: 'reboot' | 'factory-reset' | 'reset-keep-wifi') {
    setBusy(true);
    setMessage(null);
    const idByKind = { reboot: HEALTH_REBOOT, 'factory-reset': HEALTH_FACTORY_RESET, 'reset-keep-wifi': HEALTH_RESET_KEEP_WIFI };
    const okByKind = {
      reboot: t('deviceSettings.rebootSent'),
      'factory-reset': t('deviceSettings.factoryResetSent'),
      'reset-keep-wifi': t('deviceSettings.resetKeepWifiSent'),
    };
    try {
      await opCmd(deviceId, idByKind[kind]);
      setDialog(null);
      setMessage(okByKind[kind]);
    } catch (err) {
      // Reboot/FactoryReset 常導致 device 立即離線而收不到 ACK——視為已送出但提示確認
      setDialog(null);
      setMessage(err instanceof Error ? t('deviceSettings.cmdUnconfirmedReason', { msg: err.message }) : t('deviceSettings.cmdUnconfirmed'));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  return (
    <SectionCard title={t('deviceSettings.deviceInfoMaintenance')}>
      {wsOpen ? (
        <>
          <ReadonlyRow label={t('deviceSettings.connectionStatus')} attr={online} render={(v) => (v === true || v === 1 ? t('deviceSettings.online') : t('deviceSettings.offline'))} />
          <ReadonlyRow label={t('deviceSettings.firmwareVersion')} attr={fw} />
          {!phone && <ReadonlyRow label={t('deviceSettings.wifiSignal')} attr={wifi} render={(v) => `${String(v)} / 100`} />}
          <ReadonlyRow label={t('deviceSettings.deviceIp')} attr={ip} />
          {!phone && <ReadonlyRow label={t('deviceSettings.deviceTemp')} attr={temp} render={(v) => `${String(v)} °C`} />}
          {phone && (
            <RowShell label={t('deviceSettings.timezone')}>
              <span data-testid="pc-tz">{deviceTz ? t('phonecam.tzFollowsPhone', { label: labelForPosix(deviceTz) }) : '—'}</span>
            </RowShell>
          )}
          {phone && <PhoneCamRows deviceId={deviceId} isOwner={isOwner} wsOpen={wsOpen} />}
          {!phone && <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span>{t('deviceSettings.timezone')}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select
                data-testid="tz-picker"
                value={tzValue}
                disabled={!isOwner || tz.setting}
                onChange={(e) => tz.set(e.target.value)}
              >
                {/* current stored POSIX not in the curated list → show it so the value isn't lost */}
                {tzValue && !TIMEZONES.some((o) => o.posix === tzValue) && (
                  <option value={tzValue}>{tzValue}</option>
                )}
                {TIMEZONES.map((o) => (
                  <option key={`${o.iana}:${o.posix}`} value={o.posix}>{o.label}</option>
                ))}
              </select>
              {tz.setting && <span className="text-secondary">…</span>}
            </span>
          </label>}
          {tz.setError && <p className="text-danger" style={{ margin: '4px 0 0' }}>{tz.setError}</p>}
        </>
      ) : (
        <p className="text-secondary" style={{ margin: 0 }}>
          {t('deviceSettings.infoConnecting', { version: shownVersion || '—' })}
        </p>
      )}

      {/* ---- 韌體更新（REST；APP 00 §7.3/§7.4） ---- */}
      {/* 手機攝影機沒有 OTA（MyCam 由 App 商店更新；ls-loop MW） */}
      {!phone && (
      <div style={{ borderTop: '1px solid var(--color-surface-line)', paddingTop: 'var(--spacing-5)', display: 'grid', gap: 'var(--spacing-4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-6)' }}>
          <div>
            <div>{t('deviceSettings.firmwareUpdate')}</div>
            <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
              {t('deviceSettings.currentVersion', { version: shownVersion || '—' })}
            </div>
          </div>
          <button className="btn-ghost" disabled={otaState === 'checking' || otaState === 'upgrading' || otaState === 'rebooting'} onClick={() => void checkUpgrade()}>
            {otaState === 'checking' ? t('deviceSettings.checking') : t('deviceSettings.checkUpdate')}
          </button>
        </div>
        {otaState === 'up-to-date' && (
          <p className="text-secondary" role="status" style={{ margin: 0 }}>
            {t('deviceSettings.upToDate')}
          </p>
        )}
        {otaState === 'error' && otaError && (
          <p className="error-text" role="alert" style={{ margin: 0 }}>
            {otaError}
          </p>
        )}
        {otaState === 'available' && upgrade && (
          <div
            style={{
              border: '1px solid var(--color-semantic-info)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-5)',
              display: 'grid',
              gap: 'var(--spacing-3)',
            }}
          >
            <div>
              {t('deviceSettings.newVersionAvailable')} <strong>{upgrade.latest_version}</strong>
              {upgrade.is_critical && <span style={{ color: 'var(--color-semantic-danger)', marginLeft: 'var(--spacing-3)' }}>{t('deviceSettings.criticalUpdate')}</span>}
            </div>
            {upgrade.release_notes_url && (
              <a href={upgrade.release_notes_url} target="_blank" rel="noreferrer" style={{ fontSize: 'var(--font-size-sm)' }}>
                {t('deviceSettings.viewReleaseNotes')}
              </a>
            )}
            {isOwner ? (
              <button className="btn-primary" style={{ justifySelf: 'start' }} onClick={() => setDialog('ota')}>
                {t('deviceSettings.updateNow')}
              </button>
            ) : (
              <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                {t('deviceSettings.ownerOnlyUpdate')}
              </p>
            )}
          </div>
        )}
        {otaState === 'upgrading' && (
          <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
            <p style={{ margin: 0 }} role="status">
              <span aria-hidden>⬆️</span> {t('deviceSettings.updateInProgress')}
            </p>
            {otaProgress.length > 0 && (
              <pre
                className="text-secondary"
                style={{ margin: 0, fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)', whiteSpace: 'pre-wrap' }}
              >
                {otaProgress.join('\n')}
              </pre>
            )}
          </div>
        )}
        {otaState === 'rebooting' && (
          <p style={{ margin: 0 }} role="status">
            <span aria-hidden>🔄</span> {t('deviceSettings.updateRebooting')}
          </p>
        )}

        {/* ---- 更新紀錄（spec 21 §8；owner-only，GET /v3/devices/{id}/ota） ---- */}
        {otaJobs !== null && otaJobs.length > 0 && (
          <details style={{ marginTop: 'var(--spacing-2)' }}>
            <summary
              className="text-secondary"
              style={{ cursor: 'pointer', fontSize: 'var(--font-size-sm)', userSelect: 'none' }}
            >
              {t('deviceSettings.updateHistory', { count: otaJobs.length })}
            </summary>
            <ul style={{ listStyle: 'none', margin: 'var(--spacing-3) 0 0', padding: 0, display: 'grid', gap: 'var(--spacing-2)' }}>
              {otaJobs.map((job, i) => (
                <li
                  key={`${job.job_ts}-${i}`}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 'var(--spacing-4)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  <span>
                    <span style={{ fontFamily: 'var(--font-family-mono)' }}>{job.target_version || '—'}</span>
                    <span className="text-secondary" style={{ marginLeft: 'var(--spacing-3)' }}>
                      {formatTimestampUs(job.job_ts, { second: undefined })}
                    </span>
                  </span>
                  <span style={{ color: otaStatusColor(job.status), whiteSpace: 'nowrap' }}>
                    {otaStatusLabel(t, job.status) ?? job.status}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
      )}

      {/* ---- 維運指令（owner-only，§5.3；離線/未連線 disable，§2.6 不入 outbox） ---- */}
      {isOwner && phone && (
        <div style={{ borderTop: '1px solid var(--color-surface-line)', paddingTop: 'var(--spacing-5)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
          {/* MyCam：沒有重開機 / 重設 / 回復原廠（手機自己管）；只有「請攝影機回報問題」（F15，非破壞性、不用確認） */}
          <button className="btn-ghost" data-testid="pc-request-report" disabled={!wsOpen || busy} onClick={() => void requestReport()}>
            {t('phonecam.requestReport')}
          </button>
          <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>{t('phonecam.requestReportHint')}</span>
        </div>
      )}
      {isOwner && !phone && (
        <div style={{ borderTop: '1px solid var(--color-surface-line)', paddingTop: 'var(--spacing-5)', display: 'flex', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
          <button className="btn-ghost" disabled={!wsOpen || busy} onClick={() => setDialog('reboot')}>
            {t('deviceSettings.rebootDevice')}
          </button>
          <button className="btn-ghost" disabled={!wsOpen || busy} onClick={() => setDialog('reset-keep-wifi')}>
            {t('deviceSettings.resetKeepWifi')}
          </button>
          <button className="btn-danger" disabled={!wsOpen || busy} onClick={() => setDialog('factory-reset')}>
            {t('deviceSettings.factoryReset')}
          </button>
        </div>
      )}
      {message && (
        <p className="text-secondary" role="status" style={{ margin: 0 }}>
          {message}
        </p>
      )}

      {dialog === 'reboot' && (
        <ConfirmDialog
          spec={{ title: t('deviceSettings.rebootConfirmTitle'), body: t('deviceSettings.rebootConfirmBody'), confirmLabel: t('deviceSettings.reboot') }}
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() => void runCmd('reboot')}
        />
      )}
      {dialog === 'factory-reset' && (
        <ConfirmDialog
          spec={{
            title: t('deviceSettings.factoryResetConfirmTitle'),
            body: (
              <>
                <p style={{ marginTop: 0 }}>{t('deviceSettings.factoryResetConfirmBody1')}</p>
                <p style={{ marginBottom: 0 }}>
                  {t('deviceSettings.factoryResetConfirmBody2Prefix')}<strong>{t('deviceSettings.factoryResetConfirmBody2Strong')}</strong>{t('deviceSettings.factoryResetConfirmBody2Suffix')}
                </p>
              </>
            ),
            confirmLabel: t('deviceSettings.confirmReset'),
            danger: true,
            requireText: deviceName,
            requireTextHint: t('deviceSettings.typeDeviceNameHint', { name: deviceName }),
          }}
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() => void runCmd('factory-reset')}
        />
      )}
      {dialog === 'reset-keep-wifi' && (
        <ConfirmDialog
          spec={{
            title: t('deviceSettings.resetKeepWifiConfirmTitle'),
            body: (
              <>
                <p style={{ marginTop: 0 }}>{t('deviceSettings.resetKeepWifiConfirmBody1')}</p>
                <p style={{ marginBottom: 0 }}>{t('deviceSettings.resetKeepWifiConfirmBody2')}</p>
              </>
            ),
            confirmLabel: t('deviceSettings.resetKeepWifiConfirm'),
            danger: true,
          }}
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() => void runCmd('reset-keep-wifi')}
        />
      )}
      {dialog === 'ota' && upgrade && (
        <ConfirmDialog
          spec={{
            title: t('deviceSettings.otaConfirmTitle', { version: upgrade.latest_version }),
            body: t('deviceSettings.otaConfirmBody'),
            confirmLabel: t('deviceSettings.startUpdate'),
          }}
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() => void triggerOta()}
        />
      )}
    </SectionCard>
  );
}
